import type { InputSnapshot } from "./types";

type InputKey = "left" | "right" | "tuck" | "jump";

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target instanceof HTMLElement ? target : null;
  if (element?.closest("[data-input]")) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLSelectElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLButtonElement ||
    target instanceof HTMLAnchorElement ||
    (target instanceof HTMLElement && target.closest("button, a, [role='button']") !== null) ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export class InputController {
  private readonly keyboard = new Set<InputKey>();
  private readonly pulses = new Set<InputKey>();
  private readonly pointers = new Map<number, InputKey>();
  private current: InputSnapshot = { steer: 0, forward: true, tuck: false, jump: false };

  constructor(
    private readonly root: HTMLElement,
    private readonly onChange: (input: InputSnapshot) => void,
  ) {
    window.addEventListener("keydown", this.onKeyDown, { passive: false });
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.releaseAll);
    root.addEventListener("pointerdown", this.onPointerDown);
    root.addEventListener("pointerup", this.onPointerUp);
    root.addEventListener("pointercancel", this.onPointerUp);
    root.addEventListener("lostpointercapture", this.onPointerUp);
    root.addEventListener("click", this.onAccessibleClick);
  }

  get snapshot(): InputSnapshot {
    return { ...this.current };
  }

  reset(): void {
    this.releaseAll();
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.releaseAll);
    this.root.removeEventListener("pointerdown", this.onPointerDown);
    this.root.removeEventListener("pointerup", this.onPointerUp);
    this.root.removeEventListener("pointercancel", this.onPointerUp);
    this.root.removeEventListener("lostpointercapture", this.onPointerUp);
    this.root.removeEventListener("click", this.onAccessibleClick);
  }

  private keyFor(code: string): InputKey | null {
    if (code === "ArrowLeft" || code === "KeyA") return "left";
    if (code === "ArrowRight" || code === "KeyD") return "right";
    if (code === "ArrowDown" || code === "KeyS" || code === "ShiftLeft" || code === "ShiftRight") return "tuck";
    if (code === "Space") return "jump";
    return null;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isTypingTarget(event.target)) return;
    const key = this.keyFor(event.code);
    if (!key) return;
    event.preventDefault();
    this.keyboard.add(key);
    this.publish();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const key = this.keyFor(event.code);
    if (!key) return;
    this.keyboard.delete(key);
    this.publish();
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-input]");
    const key = target?.dataset.input as InputKey | undefined;
    if (!target || !key) return;
    event.preventDefault();
    target.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, key);
    target.dataset.pressed = "true";
    this.publish();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const key = this.pointers.get(event.pointerId);
    if (!key) return;
    this.pointers.delete(event.pointerId);
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-input]");
    if (target) delete target.dataset.pressed;
    this.publish();
  };

  private readonly onAccessibleClick = (event: MouseEvent): void => {
    // Pointer presses are handled continuously above. A zero-detail click is
    // keyboard/switch activation, so give it a short, useful control pulse.
    if (event.detail !== 0) return;
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-input]");
    const key = target?.dataset.input as InputKey | undefined;
    if (!target || !key) return;
    event.preventDefault();
    this.pulses.add(key);
    this.publish();
    window.setTimeout(() => {
      this.pulses.delete(key);
      this.publish();
    }, key === "tuck" ? 260 : 140);
  };

  private readonly releaseAll = (): void => {
    this.keyboard.clear();
    this.pulses.clear();
    this.pointers.clear();
    this.root.querySelectorAll<HTMLElement>("[data-input][data-pressed]").forEach((element) => {
      delete element.dataset.pressed;
    });
    this.publish();
  };

  private active(key: InputKey): boolean {
    if (this.keyboard.has(key)) return true;
    if (this.pulses.has(key)) return true;
    for (const pointerKey of this.pointers.values()) if (pointerKey === key) return true;
    return false;
  }

  private publish(): void {
    const next = {
      steer: Number(this.active("right")) - Number(this.active("left")),
      // A water-slide racer should never strand a player at the start because
      // a held key, pointer capture, or Discord iframe focus was interrupted.
      forward: true,
      tuck: this.active("tuck"),
      jump: this.active("jump"),
    };
    if (
      next.steer === this.current.steer &&
      next.forward === this.current.forward &&
      next.tuck === this.current.tuck &&
      next.jump === this.current.jump
    ) {
      return;
    }
    this.current = next;
    this.onChange({ ...next });
  }
}
